// src/pages/admin-audit-management/components/CompletedSubmissionsTable.jsx
import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import StatusBadge from '../../user-dashboard/components/StatusBadge';
import { storageService } from '../../../services/storageService';
import toast from 'react-hot-toast';
import TableSkeleton from '../../../components/ui/TableSkeleton';

// ─── Truncate long HTML filenames to a readable short form ───────────────────
const truncateFileName = (name = '', maxLen = 28) => {
  if (!name) return '—';
  const ext = name.includes('.') ? '.' + name.split('.').pop() : '';
  const base = ext ? name.slice(0, name.lastIndexOf('.')) : name;
  if (base.length <= maxLen) return name;
  return base.slice(0, maxLen - 1) + '…' + ext;
};

const CompletedSubmissionsTable = ({ audits, onViewDetails }) => {
  const [sortField, setSortField]               = useState('publishedDate');
  const [sortDirection, setSortDirection]       = useState('desc');
  const [expandedRows, setExpandedRows]         = useState(new Set());
  const [downloadingFiles, setDownloadingFiles] = useState(new Set());
  const [isLoading]                             = useState(false);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const toggleRowExpansion = (auditId) => {
    const next = new Set(expandedRows);
    next.has(auditId) ? next.delete(auditId) : next.add(auditId);
    setExpandedRows(next);
  };

  const sortedAudits = [...(audits || [])].sort((a, b) => {
    let aVal = a?.[sortField];
    let bVal = b?.[sortField];
    if (['submissionDate', 'registrationDate', 'publishedDate'].includes(sortField)) {
      aVal = new Date(aVal).getTime();
      bVal = new Date(bVal).getTime();
    }
    if (aVal === undefined) aVal = '';
    if (bVal === undefined) bVal = '';
    return sortDirection === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const getAuditTypeDisplay = (status) =>
    status?.toLowerCase() === 'paid' ? 'Paid Audit' : 'Free Audit';

  const fmtDateIST = (d) => {
    if (!d) return { date: null, time: '', tz: '' };
    const dt = new Date(d);
    const date = dt.toLocaleDateString('en-IN', {
      month: 'short', day: 'numeric', year: 'numeric',
      timeZone: 'Asia/Kolkata',
    });
    const time = dt.toLocaleTimeString('en-IN', {
      hour: '2-digit', minute: '2-digit', hour12: true,
      timeZone: 'Asia/Kolkata',
    });
    return { date, time, tz: 'IST' };
  };

  const handleDownloadDeck = async (audit) => {
    if (!audit?.fileUrl) { toast.error('No file available for download'); return; }
    if (audit.fileUrl.startsWith('http')) { window.open(audit.fileUrl, '_blank'); return; }
    try {
      setDownloadingFiles(prev => new Set(prev).add(audit.id));
      const signedUrl = await storageService.getDeckFileSignedUrl(audit.fileUrl);
      window.open(signedUrl, '_blank');
      toast.success('Download started', { icon: '📥' });
    } catch (error) {
      if (error?.message?.includes('not found'))              toast.error('File not found in storage.');
      else if (error?.message?.includes('Invalid file path')) toast.error('Invalid file reference.');
      else toast.error('Failed to download deck file.');
    } finally {
      setDownloadingFiles(prev => { const s = new Set(prev); s.delete(audit.id); return s; });
    }
  };

  const getFileName = (audit) =>
    audit?.fileName || audit?.html_storage_path?.split('/')?.pop() || audit?.file_name || '';

  const SortIcon = ({ field }) => (
    <Icon
      name={sortField === field ? (sortDirection === 'asc' ? 'ChevronUp' : 'ChevronDown') : 'ChevronsUpDown'}
      size={14}
      className="opacity-50"
    />
  );

  const ThBtn = ({ field, children, className = '' }) => (
    <button
      onClick={() => handleSort(field)}
      className={`flex items-center gap-1.5 font-semibold text-xs text-foreground hover:text-primary transition-smooth whitespace-nowrap ${className}`}
    >
      {children}
      <SortIcon field={field} />
    </button>
  );

  if (isLoading) return <TableSkeleton rows={5} columns={7} showAvatar />;

  if (!audits || audits.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border flex flex-col items-center justify-center py-16 px-4">
        <Icon name="Inbox" size={40} className="text-muted-foreground mb-3" />
        <p className="text-sm font-medium text-muted-foreground">No completed audits</p>
        <p className="text-xs text-muted-foreground mt-1">Completed audits will appear here</p>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full table-fixed min-w-[960px]">
          <colgroup>
            <col style={{ width: 36 }} />
            <col style={{ width: '18%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '13%' }} />
            <col style={{ width: '15%' }} />
            <col style={{ width: '11%' }} />
            <col style={{ width: '10%' }} />
            <col style={{ width: '12%' }} />
          </colgroup>

          <thead className="bg-muted/40 border-b border-border">
            <tr>
              <th className="px-3 py-3" />
              <th className="px-3 py-3 text-left"><ThBtn field="userName">User</ThBtn></th>
              <th className="px-3 py-3 text-left hidden md:table-cell"><ThBtn field="requestId">Request ID</ThBtn></th>

              {/* Payment Status */}
              <th className="px-3 py-3 text-left hidden md:table-cell"><ThBtn field="paymentStatus">Payment Status</ThBtn></th>

              {/* ── RENAMED: Presentation → Deck Submitted ── */}
              <th className="px-3 py-3 text-left hidden lg:table-cell"><ThBtn field="presentationTitle">Deck Submitted</ThBtn></th>

              <th className="px-3 py-3 text-left hidden xl:table-cell"><ThBtn field="fileName">Audit Report</ThBtn></th>
              <th className="px-3 py-3 text-left hidden lg:table-cell"><ThBtn field="publishedDate">Published</ThBtn></th>
              <th className="px-3 py-3 text-left text-xs font-semibold text-foreground whitespace-nowrap">Status</th>
              <th className="px-3 py-3 text-right text-xs font-semibold text-foreground">Actions</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border">
            {sortedAudits.map((audit) => (
              <React.Fragment key={audit?.id}>
                <tr className="hover:bg-muted/20 transition-smooth">

                  {/* Expand toggle */}
                  <td className="px-3 py-3">
                    <button
                      onClick={() => toggleRowExpansion(audit?.id)}
                      className="text-muted-foreground hover:text-foreground transition-smooth"
                      title="View details"
                    >
                      <Icon name={expandedRows.has(audit?.id) ? 'ChevronDown' : 'ChevronRight'} size={16} />
                    </button>
                  </td>

                  {/* User */}
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <Image
                        src={audit?.userAvatar}
                        alt={audit?.userAvatarAlt}
                        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-foreground truncate leading-tight">
                          {audit?.userName}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{audit?.userEmail}</p>
                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium border mt-0.5 ${
                          audit?.auditType === 'paid'
                            ? 'bg-success/10 text-success border-success/20'
                            : 'bg-muted text-muted-foreground border-border'
                        }`}>
                          <Icon name={audit?.auditType === 'paid' ? 'Crown' : 'User'} size={9} />
                          {getAuditTypeDisplay(audit?.auditType)}
                        </span>
                      </div>
                    </div>
                  </td>

                  {/* Request ID */}
                  <td className="px-3 py-3 hidden md:table-cell">
                    <p className="font-semibold text-sm text-primary truncate">{audit?.requestId || 'N/A'}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {audit?.auditType?.toUpperCase() || 'FREE'}
                    </p>
                  </td>

                  {/* Payment Status */}
                  <td className="px-3 py-3 hidden md:table-cell">
                    {audit?.auditType === 'free' ? (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', background: '#f8fafc', border: '1px solid #e2e8f0', color: '#94a3b8' }}>
                        — No Payment
                      </span>
                    ) : (audit?.paymentStatus === 'captured' || audit?.paymentStatus === 'received') ? (
                      <div>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', background: '#dcfce7', border: '1px solid #86efac', color: '#166534' }}>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
                          Received
                        </span>
                        {audit?.razorpayPaymentId && (
                          <p style={{ margin: '4px 0 0', fontSize: '11px', color: '#94a3b8', fontFamily: 'ui-monospace, monospace', letterSpacing: '-0.01em' }}>
                            {audit.razorpayPaymentId}
                          </p>
                        )}
                      </div>
                    ) : (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: 500, whiteSpace: 'nowrap', background: '#fef2f2', border: '1px solid #fca5a5', color: '#dc2626' }}>
                        <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        Failed
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-3 hidden lg:table-cell">
                    <p className="font-medium text-sm text-foreground truncate">
                      {audit?.presentationTitle}
                    </p>
                    {audit?.deckFileName && (
                      <div className="flex items-center gap-1 mt-0.5">
                        <Icon name="FileText" size={11} className="text-muted-foreground flex-shrink-0" />
                        <span className="text-xs text-muted-foreground truncate">{audit?.deckFileName}</span>
                      </div>
                    )}
                  </td>

                  {/* Audit Report */}
                  <td className="px-3 py-3 hidden xl:table-cell">
                    {getFileName(audit) ? (
                      <div className="min-w-0">
                        <div className="flex items-center gap-1 min-w-0">
                          <Icon name="FileCode" size={11} className="text-success flex-shrink-0" />
                          <span
                            className="text-xs text-success font-medium truncate"
                            title={getFileName(audit)}
                          >
                            {truncateFileName(getFileName(audit), 26)}
                          </span>
                        </div>
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-success/10 rounded text-xs text-success mt-0.5">
                          <Icon name="CheckCircle2" size={10} />
                          Published
                        </span>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground">—</span>
                    )}
                  </td>

                  {/* Published */}
                  <td className="px-3 py-3 hidden lg:table-cell">
                    {(() => {
                      const { date, time, tz } = fmtDateIST(audit?.publishedDate);
                      if (!date) return <span className="text-xs text-muted-foreground">—</span>;
                      return (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round">
                              <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                              <line x1="16" y1="2" x2="16" y2="6"/>
                              <line x1="8" y1="2" x2="8" y2="6"/>
                              <line x1="3" y1="10" x2="21" y2="10"/>
                            </svg>
                            <span style={{ fontSize: '13px', fontWeight: 500, color: '#374151' }}>{date}</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round">
                              <circle cx="12" cy="12" r="10"/>
                              <polyline points="12 6 12 12 16 14"/>
                            </svg>
                            <span style={{ fontSize: '12px', color: '#64748b' }}>{time}</span>
                            <span style={{ fontSize: '10px', fontWeight: 600, color: '#0082FB', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '3px', padding: '1px 6px', letterSpacing: '0.04em', lineHeight: '18px' }}>
                              {tz}
                            </span>
                          </div>
                        </div>
                      );
                    })()}
                  </td>

                  {/* Status */}
                  <td className="px-3 py-3 whitespace-nowrap">
                    <StatusBadge status="completed" />
                  </td>

                  {/* Actions */}
                  <td className="px-3 py-3">
                    <div className="flex flex-col xl:flex-row items-stretch xl:items-center justify-end gap-1.5">
                      {audit?.fileUrl && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadDeck(audit)}
                          iconName={downloadingFiles.has(audit?.id) ? 'Loader2' : 'Download'}
                          iconPosition="left"
                          disabled={downloadingFiles.has(audit?.id)}
                          className="shrink-0 min-w-[68px] w-full xl:w-auto justify-center"
                        >
                          {downloadingFiles.has(audit?.id) ? '…' : 'Deck'}
                        </Button>
                      )}
                      <button
                        onClick={() => audit?.htmlUrl && onViewDetails?.(audit)}
                        disabled={!audit?.htmlUrl}
                        title={audit?.htmlUrl ? 'View audit report' : 'Report not available'}
                        style={{
                          display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 5,
                          padding: '6px 12px', borderRadius: '7px',
                          border: '1px solid #e2e8f0',
                          background: audit?.htmlUrl ? '#fff' : '#f8fafc',
                          fontSize: '13px', fontWeight: 500,
                          color: audit?.htmlUrl ? '#374151' : '#cbd5e1',
                          cursor: audit?.htmlUrl ? 'pointer' : 'not-allowed',
                          opacity: audit?.htmlUrl ? 1 : 0.5,
                          transition: 'background 0.15s, border-color 0.15s',
                          whiteSpace: 'nowrap',
                        }}
                        onMouseEnter={e => { if (audit?.htmlUrl) { e.currentTarget.style.background = '#f8fafc'; e.currentTarget.style.borderColor = '#cbd5e1'; } }}
                        onMouseLeave={e => { if (audit?.htmlUrl) { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#e2e8f0'; } }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
                          stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                        View
                      </button>
                    </div>
                  </td>
                </tr>

                {/* ── Expanded detail row ── */}
                {expandedRows.has(audit?.id) && (
                  <tr className="bg-muted/10">
                    <td colSpan={9} className="px-4 py-4">
                      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-4 bg-card rounded-lg border border-border">

                        <DetailField icon="Hash" label="Request ID">
                          <span className="text-sm font-semibold text-primary">{audit?.requestId || 'N/A'}</span>
                        </DetailField>

                        <DetailField icon="User" label="Full Name">
                          <span className="text-sm font-medium text-foreground">{audit?.userName || 'N/A'}</span>
                        </DetailField>

                        <DetailField icon="Mail" label="Email">
                          <span className="text-sm font-medium text-foreground truncate block">{audit?.userEmail || 'N/A'}</span>
                        </DetailField>

                        <DetailField icon="Crown" label="Audit Type">
                          <span className="text-sm font-medium text-foreground capitalize">{getAuditTypeDisplay(audit?.auditType)}</span>
                        </DetailField>

                        <DetailField icon="CreditCard" label="Payment Status">
                          <span className="text-sm font-medium text-foreground">
                            {audit?.auditType === 'free' ? 'No Payment' :
                             (audit?.paymentStatus === 'captured' || audit?.paymentStatus === 'received') ? 'Received' :
                             'Failed'}
                          </span>
                        </DetailField>

                        <DetailField icon="Hash" label="Payment ID">
                          <span className="text-xs font-mono text-foreground">
                            {audit?.auditType === 'free' ? '—' : (audit?.razorpayPaymentId || '—')}
                          </span>
                        </DetailField>

                        <DetailField icon="Calendar" label="Submitted">
                          <span className="text-sm font-medium text-foreground">{fmtDateIST(audit?.submissionDate).date ? `${fmtDateIST(audit?.submissionDate).date}, ${fmtDateIST(audit?.submissionDate).time} IST` : 'N/A'}</span>
                        </DetailField>

                        <DetailField icon="Calendar" label="Published">
                          <span className="text-sm font-medium text-foreground">{fmtDateIST(audit?.publishedDate).date ? `${fmtDateIST(audit?.publishedDate).date}, ${fmtDateIST(audit?.publishedDate).time} IST` : 'N/A'}</span>
                        </DetailField>

                        <DetailField icon="CheckCircle2" label="Status">
                          <StatusBadge status="completed" />
                        </DetailField>

                        <DetailField icon="Layers" label="Slides">
                          <span className="text-sm font-medium text-foreground">{audit?.slideCount || 0} slides</span>
                        </DetailField>

                        <DetailField icon="FileText" label="Audit File" className="col-span-2">
                          <span
                            className="text-xs font-medium text-foreground truncate block font-mono"
                            title={getFileName(audit)}
                          >
                            {getFileName(audit) || 'N/A'}
                          </span>
                        </DetailField>

                        <DetailField icon="Tag" label="Deck Submitted" className="col-span-2">
                          <span className="text-sm font-medium text-foreground truncate block">{audit?.presentationTitle || 'N/A'}</span>
                        </DetailField>

                        <DetailField icon="Hash" label="User ID" className="col-span-2 lg:col-span-4">
                          <span className="text-xs font-mono text-foreground truncate block">{audit?.userId || 'N/A'}</span>
                        </DetailField>

                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ─── Small helper for expanded detail cells ───────────────────────────────────
const DetailField = ({ icon, label, children, className = '' }) => (
  <div className={`space-y-0.5 min-w-0 ${className}`}>
    <p className="text-xs font-medium text-muted-foreground flex items-center gap-1">
      <Icon name={icon} size={11} />
      {label}
    </p>
    {children}
  </div>
);

export default CompletedSubmissionsTable;